<template>
  <div>
    <div
      v-if="salesContactPhone"
      class="sticky sales-contact"
      :class="{'no-experimental': !experimentalModeVisible}"
      data-track="show-phone-number-sidebutton"
      @click="copyPhoneToClipboard"
    >
      <div class="sticky__sales-contact">
        <div class="k-button k-button-md k-button-solid">
          <div class="svg-box">
            <CommunicationPhone />
          </div>
        </div>
        <div class="sticky__info">
          <div class="phone-content">
            <div class="phone-label">
              {{ $tv('salesContact', 'navigation') }}
            </div>
            <div class="phone-row">
              <div
                class="phone-number"
                :title="salesContactPhone"
              >
                {{ salesContactPhone }}
              </div>
              <button
                class="copy-btn"
              >
                <ActionCopy />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="experimentalModeVisible"
      class="sticky experimental-features"
      :class="{'no-feedback': !feedbackButtonVisible, 'has-sales-contact': !!salesContactPhone}"
    >
      <div class="sticky__experimental-features">
        <div class="k-button k-button-md k-button-solid">
          <div class="svg-box">
            <component :is="experimentalSvg" />
          </div>
          <span>{{ $tv('mode', 'navigation') }}</span>
        </div>
        <div class="sticky__info">
          <div>
            <ToggleSwitch
              :is-checked="experimentalMode"
              :label-left="$tv('expMode', 'navigation')"
              @change-value="switchExperimentalMode"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ToggleSwitch from '~/components/input-components/ToggleSwitch'
import CommunicationEmail from '~/assets/icons/communication_email.svg?component'
import CommunicationPhone from '~/assets/icons/communication_phone.svg?component'
import ActionCopy from '~/assets/icons/action-copy.svg?component'
import Science from '~/assets/icons/science.svg?component'
import ScienceOff from '~/assets/icons/science_off.svg?component'
import {SIDEBAR_ACTIONS, CONFIGURATION_KEYS, CUSTOMER_SETTING_KEYS} from '~/assets/js/constants'
import {useConfigurationDataStore} from '@/store/configuration-data'
import {useCustomerSettingsStore} from '@/store/customer-settings'
import {storeToRefs} from 'pinia'
import {useGtm} from '@gtm-support/vue-gtm'
import {useEventItemConverter} from '@/composables/useEventItemConverter'

export default {
  name: 'SideButtons',
  components: {CommunicationEmail, CommunicationPhone, ActionCopy, ToggleSwitch},
  setup() {
    const {experimentalMode, configurations} = storeToRefs(useConfigurationDataStore())
    const {currentCustomerSettings} = storeToRefs(useCustomerSettingsStore())
    const route = useRoute()
    return {
      experimentalMode,
      configurations,
      currentCustomerSettings,
      route
    }
  },
  data: function() {
    return {
      contactAction: SIDEBAR_ACTIONS.Contact,
      feedbackAction: SIDEBAR_ACTIONS.Feedback
    }
  },
  computed: {
    // #37969 comment in when needed
    //feedbackButtonVisible() {
    //  return false
    //},
    experimentalSvg() {
      if (this.experimentalMode) {
        return Science
      } else {
        return ScienceOff
      }
    },
    experimentalModeVisible() {
      // TODO #32699 turn back on/off when needed
      return this.configurations?.find(c => c.key === CONFIGURATION_KEYS.SHOW_EXPERIMENTAL)?.value === 'True'
    },
    salesContactPhone() {
      const setting = this.currentCustomerSettings?.find(s => s.translationKey === CUSTOMER_SETTING_KEYS.SalesContactPhone)
      return setting?.customerSettingValue || ''
    }
  },
  methods: {
    emitClick(action) {
      this.$emit('click', action)
    },
    switchExperimentalMode(val) {
      this.experimentalMode = !!val
    },
    async copyPhoneToClipboard() {
      if (this.salesContactPhone) {
        await navigator.clipboard.writeText(this.salesContactPhone)
        useGtm()?.trackEvent(useEventItemConverter().convert({
          event: 'show_phone_number_click',
          add_personalization: true,
          props: {page: this.route?.path || ''}
        }))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
// Width configuration - adjust these values to change widths globally
$button-width: 4.0625rem;
$info-panel-width: 12.4375rem; // Max width for info panel
$info-panel-width-default: $info-panel-width - 1rem; // Default smaller width (calculated)
$total-width: $button-width + $info-panel-width;

.sticky {
  display: block;
  overflow: hidden;
  position: fixed;
  right: 0;
  max-width: $total-width;
  width: auto;
  height: 4.0625rem;
  cursor: pointer;
  text-transform: uppercase;
  z-index: 1000;
  bottom: 55vh;
  transition: transform .3s ease-in-out, -webkit-transform .3s ease-in-out;
  transform: translate3d($info-panel-width, 0, 0);

  &.feedback {
    bottom: 50vh;
  }

  &.sales-contact {
    display: flex;
    bottom: 50vh;
    max-width: $total-width;
    width: auto;
    transform: translateX(calc(100% - #{$button-width}));

    &.no-experimental {
      bottom: 50vh;
    }
  }

  &.experimental-features {
    bottom: 45vh;
    width: $total-width;
    transform: translate3d($info-panel-width, 0, 0);

    &.no-feedback {
      bottom: 50vh;
    }

    &.has-sales-contact {
      bottom: 42vh;
    }
  }

  &__sales-contact {
    display: flex;
    max-width: $total-width;
    width: auto;

    .k-button,
    .sticky__info {
      background-color: $color__primary;
    }

    .sticky__info {
      display: flex;
      align-items: center;
      width: fit-content;
      max-width: $info-panel-width;

      .phone-content {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        width: fit-content;
        margin-right: .5rem;
      }

      .phone-label {
        color: $color__white;
        font-size: 0.75rem;
        font-weight: $base-text-font-weight-light;
        text-transform: none;
      }

      .phone-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.25rem;
        margin-bottom: -.25rem;
      }

      .phone-number {
        color: $color__white;
        font-size: $base-text-font-size;
        font-weight: $base-text-font-weight-semi-bold;
        text-transform: none;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1 1 auto;
        min-width: 0;
        max-width: 8rem;
      }

      .copy-btn {
        background: none;
        border: none;
        padding: .25rem;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        svg {
          width: 1rem;
          height: 1rem;
          fill: $color__white;
          color: $color__white;
        }

        &:hover {
          opacity: 0.8;
        }
      }
    }

    .k-button span {
      position: relative;
      top: -.35rem;
    }

    .svg-box {
      display: flex;
    }

    svg {
      width: 1.5rem;
      height: 1.5rem;
    }
  }

  &__experimental-features {
    .k-button,
    .sticky__info {
      background-color: $color__secondary;
    }

    .sticky__info {
      width: $info-panel-width;
      display: flex;
      align-items: center;

      > div {
        margin-right: .5rem;
      }
    }

    .k-button span {
      position: relative;
      top: -.35rem;
    }

    svg {
      width: 2rem;
      height: 2rem;
    }
  }

  &:hover {
    transform: translateZ(0);
  }

  &__contact {
    .k-button,
    &__info {
      background-color: $color__secondary;
    }

    .icon--svg {
      font-size: 2rem;
      width: 2rem;
    }
  }

  &__feedback {
    .k-button,
    &__info {
      background-color: $color__primary;
    }
  }

  &__feedback .svg-box {
    margin-bottom: .188rem;
    height: 1.75rem;
    width: 1.75rem;
  }

  .k-button {
    width: $button-width;
    min-width: 0;
    float: left;
    flex-direction: column;
    display: flex;
    text-align: center;
    font-size: map-get($base-text-button, font-size);
    letter-spacing: -.0313rem;
    text-transform: none;
    color: $color__white;

    .icon--svg {
      font-size: 2.1875rem;
    }
  }

  &__info {
    float: left;
    display: table-cell;
    width: auto;
    max-width: $info-panel-width;
    height: 4.0625rem;
    text-align: center;
    vertical-align: middle;
    background-color: $color__secondary;

    &.feedback {
      background-color: $color__primary;
    }

    &__text {
      display: inline-block;
      position: relative;
      top: 50%;
      transform: translateY(-50%);
      width: 100%;
      color: $color__white;
      text-decoration: underline;
      padding-right: .625rem;
      font-weight: $base-text-font-weight-light;
    }
  }

  .hidden {
    display: none;
  }
}
</style>

<style lang="scss">
.sticky__experimental-features {
  .toggle-switch-container > span {
    color: $color__white;
    font-size: $base-text-font-size;
    padding-right: .2rem;
    font-weight: $base-text-font-weight-semi-bold;
  }
}
</style>
